import { cn } from '../cn';

import type { InputHTMLAttributes, ReactNode } from 'react';

export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'size'
> {
  value?: string | undefined;
  /** Receives the new string, not the event (as in the design bundle). */
  onChange?: ((value: string) => void) | undefined;
  error?: boolean | undefined;
  /** An `<Icon />` shown before the text. */
  icon?: ReactNode | undefined;
  /** Shown after the text, for example a show-password button. */
  trailing?: ReactNode | undefined;
  /** Class for the outer pill, for example a width. */
  className?: string | undefined;
}

/** Single-line text field. 40 px tall, fully rounded, focus ring from the tokens. */
export function Input({
  value,
  onChange,
  error = false,
  icon,
  trailing,
  disabled = false,
  className,
  ...rest
}: InputProps) {
  return (
    <div
      className={cn(
        'flex h-10 items-center gap-2 rounded-full border px-4 transition-shadow duration-150',
        disabled
          ? 'border-border-disabled bg-surface-static-disabled'
          : error
            ? 'border-border-danger-default bg-surface-static-default'
            : 'border-border-default bg-surface-static-default focus-within:border-border-primary-accented focus-within:shadow-focus-ring',
        className,
      )}
    >
      {icon ?? null}
      <input
        value={value}
        disabled={disabled}
        aria-invalid={error || undefined}
        onChange={(e) => onChange?.(e.target.value)}
        className="ds-body-sm min-w-0 flex-1 border-none bg-transparent text-content-default outline-none placeholder:text-content-placeholder disabled:cursor-not-allowed"
        {...rest}
      />
      {trailing ?? null}
    </div>
  );
}
